import { FeatureFlags } from 'src/environments/environment.interface';
import { EmployeeState, Status } from '../enums';
import { InstructorDetails } from '../meta';
import { Session, Studio } from './session-details';

/** Shown instead of opening the host class-details booking flow. */
export const PARTNER_BOOKING_UNAVAILABLE_MESSAGE =
  'This class is at a partner studio. Booking is not available on the website yet.';

export interface PartnerLocationOption {
  key: string;
  handle: string;
  partnerLocationId: number | null;
  name: string;
}

function readBool(value: unknown): boolean {
  if (value === true || value === 1) {
    return true;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
}

function readString(source: Record<string, unknown> | null | undefined, ...keys: string[]): string {
  if (!source) {
    return '';
  }
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return '';
}

function readId(source: Record<string, unknown> | null | undefined, ...keys: string[]): number | null {
  if (!source) {
    return null;
  }
  for (const key of keys) {
    const value = source[key];
    if (value == null || value === '') {
      continue;
    }
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
}

function asRecord(source: unknown): Record<string, unknown> | null {
  if (!source || typeof source !== 'object') {
    return null;
  }
  return source as Record<string, unknown>;
}

/** Partner handle from PascalCase, camelCase, or snake_case public payloads. */
export function partnerHandle(source: unknown): string {
  return readString(asRecord(source), 'PartnerHandle', 'partnerHandle', 'partner_handle');
}

export function partnerLocationId(source: unknown): number | null {
  return readId(asRecord(source), 'PartnerLocationId', 'partnerLocationId', 'partner_location_id');
}

/**
 * True when the row is a synced partner artifact.
 * A missing boolean still counts when a partner handle is present.
 */
export function isPartnerArtifact(source: unknown): boolean {
  const record = asRecord(source);
  if (!record) {
    return false;
  }
  if (readBool(record['IsPartnerArtifact'] ?? record['isPartnerArtifact'] ?? record['is_partner_artifact'])) {
    return true;
  }
  return partnerHandle(record).length > 0;
}

export function humanizePartnerHandle(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return trimmed;
  }
  if (trimmed.toLowerCase() === 'fitnessfactory') {
    return 'Fitness Factory';
  }
  return trimmed
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/\s+/)
    .filter(word => word.length > 0)
    .map(word => (word.length === 1 ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
}

/** Partner brand, then the studio name already on the row. */
export function partnerDisplayName(source: unknown): string {
  const record = asRecord(source);
  const explicit = readString(
    record,
    'PartnerName',
    'partnerName',
    'partner_name',
    'PartnerStudioName',
    'partnerStudioName',
    'DisplayName',
    'displayName',
    'display_name'
  );
  if (explicit) {
    return explicit;
  }
  const handle = partnerHandle(source);
  if (handle) {
    return humanizePartnerHandle(handle);
  }
  return readString(record, 'StudioName', 'studioName', 'Name', 'name') || 'Partner studio';
}

export function sessionVenueName(session: unknown): string {
  const record = asRecord(session);
  if (isPartnerArtifact(session)) {
    return partnerDisplayName(session);
  }
  return readString(record, 'StudioName', 'studioName');
}

/**
 * Reserved `partner` query token for the host schedule.
 * Partner keys are `handle` or `handle-locationId`, so this token is not a location key.
 */
export const HOST_SCHEDULE_TOKEN = 'host';

export interface ScheduleViewSelection {
  includeHost: boolean;
  partnerKeys: string[];
}

/** Host only. This is what a URL with no `partner` param means. */
export function defaultScheduleSelection(): ScheduleViewSelection {
  return { includeHost: true, partnerKeys: [] };
}

function uniquePartnerKeys(keys: string[] | null | undefined): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const key of keys ?? []) {
    const trimmed = (key ?? '').trim();
    if (!trimmed || trimmed.toLowerCase() === HOST_SCHEDULE_TOKEN) {
      continue;
    }
    if (seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    result.push(trimmed);
  }
  result.sort((a, b) => a.localeCompare(b));
  return result;
}

/** Empty or missing input falls back to host only. Nothing selected is not a stored state. */
export function normalizeScheduleSelection(
  selection: ScheduleViewSelection | null | undefined
): ScheduleViewSelection {
  const partnerKeys = uniquePartnerKeys(selection?.partnerKeys);
  if (!selection?.includeHost && partnerKeys.length === 0) {
    return defaultScheduleSelection();
  }
  return { includeHost: !!selection?.includeHost, partnerKeys };
}

/** Reads a `partner` query value. A bare partner key stays partner-only so older links keep working. */
export function parseScheduleSelection(partnerParam: string | null | undefined): ScheduleViewSelection {
  const raw = (partnerParam ?? '').trim();
  if (!raw) {
    return defaultScheduleSelection();
  }
  const parts = raw.split(',').map(part => part.trim()).filter(part => part.length > 0);
  const includeHost = parts.some(part => part.toLowerCase() === HOST_SCHEDULE_TOKEN);
  return normalizeScheduleSelection({
    includeHost,
    partnerKeys: parts,
  });
}

export function readPartnerQueryParam(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map(item => (item == null ? '' : String(item))).filter(part => part.length > 0).join(',');
  }
  if (value == null) {
    return '';
  }
  return String(value);
}

/**
 * Empty string means host only, so existing host-only URLs stay unchanged.
 * `host` is included only when at least one partner is also selected.
 */
export function formatScheduleSelection(selection: ScheduleViewSelection | null | undefined): string {
  const normalized = normalizeScheduleSelection(selection);
  if (normalized.includeHost && normalized.partnerKeys.length === 0) {
    return '';
  }
  const parts = [
    ...(normalized.includeHost ? [HOST_SCHEDULE_TOKEN] : []),
    ...normalized.partnerKeys,
  ];
  return parts.join(',');
}

export function coerceScheduleSelection(
  selection: ScheduleViewSelection | string | null | undefined
): ScheduleViewSelection {
  if (selection && typeof selection === 'object') {
    return normalizeScheduleSelection(selection);
  }
  return parseScheduleSelection(typeof selection === 'string' ? selection : '');
}

export function selectionIncludesHost(
  selection: ScheduleViewSelection | string | null | undefined
): boolean {
  return coerceScheduleSelection(selection).includeHost;
}

export function toggleHostInSelection(selection: ScheduleViewSelection): ScheduleViewSelection {
  return normalizeScheduleSelection({
    includeHost: !selection.includeHost,
    partnerKeys: selection.partnerKeys,
  });
}

export function togglePartnerInSelection(
  selection: ScheduleViewSelection,
  key: string
): ScheduleViewSelection {
  const partnerKey = (key ?? '').trim();
  const partnerKeys = selection.partnerKeys.includes(partnerKey)
    ? selection.partnerKeys.filter(existing => existing !== partnerKey)
    : [...selection.partnerKeys, partnerKey];
  return normalizeScheduleSelection({
    includeHost: selection.includeHost,
    partnerKeys,
  });
}

/** Stable key for one partner location. Host rows return an empty string. */
export function partnerScheduleKey(source: unknown): string {
  if (!isPartnerArtifact(source)) {
    return '';
  }
  const handle = partnerHandle(source).toLowerCase();
  if (!handle) {
    return '';
  }
  const locationId = partnerLocationId(source);
  return locationId == null ? handle : `${handle}-${locationId}`;
}

export function syntheticStudioIdForKey(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (Math.imul(31, hash) + key.charCodeAt(i)) | 0;
  }
  const id = -Math.abs(hash);
  return id === 0 ? -1 : id;
}

/**
 * Partner schedule visibility from `FeatureFlags.FEATURE_SHARED_SCHEDULE`.
 * Missing or disabled: nobody. Enabled with no member ids: everybody, including
 * anonymous visitors. Enabled with member ids: only those logged-in members.
 */
export function canSeeSharedSchedule(featureFlags: FeatureFlags | undefined, memberId: number | null | undefined): boolean {
  const flag = featureFlags?.FEATURE_SHARED_SCHEDULE;
  if (!flag || flag === false) {
    return false;
  }
  if (flag === true) {
    return true;
  }
  if (flag.enabled !== true) {
    return false;
  }
  const ids = (flag.memberIds ?? [])
    .map(id => Number(id))
    .filter(id => Number.isFinite(id) && id > 0);
  if (ids.length === 0) {
    return true;
  }
  const member = Number(memberId);
  if (!Number.isFinite(member) || member <= 0) {
    return false;
  }
  return ids.includes(member);
}

export function excludePartnerArtifacts<T>(items: T[] | null | undefined): T[] {
  return (items ?? []).filter(item => !isPartnerArtifact(item));
}

/**
 * Sessions for the selected locations.
 * Empty / missing selection: host only.
 * A bare partner key (no `host` token): that partner only, matching older exclusive links.
 * `host` plus partner keys: those schedules together.
 */
export function sessionsForScheduleView<T>(
  sessions: T[] | null | undefined,
  selection: ScheduleViewSelection | string | null | undefined
): T[] {
  const rows = sessions ?? [];
  const view = coerceScheduleSelection(selection);
  const keys = new Set(view.partnerKeys);
  return rows.filter(session => {
    if (!isPartnerArtifact(session)) {
      return view.includeHost;
    }
    const key = partnerScheduleKey(session);
    return key.length > 0 && keys.has(key);
  });
}

/** Partner location chips. Hidden entirely when the shared-schedule flag does not allow them. */
export function partnerLocationsForViewer(
  sessions: unknown[] | null | undefined,
  sharedScheduleAllowed: boolean
): PartnerLocationOption[] {
  if (!sharedScheduleAllowed) {
    return [];
  }
  return derivePartnerLocations(sessions);
}

/** Studios the visitor can filter, limited to the locations currently selected. */
export function studiosForScheduleSelection(
  hostStudios: Studio[] | null | undefined,
  studioByPartner: Record<string, Studio> | null | undefined,
  selection: ScheduleViewSelection | string | null | undefined
): Studio[] {
  const view = coerceScheduleSelection(selection);
  const studios: Studio[] = [];
  const seen = new Set<number>();
  const add = (studio: Studio | null | undefined) => {
    if (!studio || seen.has(studio.Id)) {
      return;
    }
    seen.add(studio.Id);
    studios.push(studio);
  };
  if (view.includeHost) {
    for (const studio of hostStudios ?? []) {
      add(studio);
    }
  }
  for (const key of view.partnerKeys) {
    add(studioByPartner?.[key]);
  }
  return studios;
}

/** Instructors the visitor can filter, limited to the locations currently selected. */
export function instructorsForScheduleSelection(
  hostInstructors: InstructorDetails[] | null | undefined,
  instructorsByPartner: Record<string, InstructorDetails[]> | null | undefined,
  selection: ScheduleViewSelection | string | null | undefined
): InstructorDetails[] {
  const view = coerceScheduleSelection(selection);
  const byId = new Map<number, InstructorDetails>();
  const add = (rows: InstructorDetails[] | null | undefined) => {
    for (const instructor of rows ?? []) {
      if (!byId.has(instructor.Id)) {
        byId.set(instructor.Id, instructor);
      }
    }
  };
  if (view.includeHost) {
    add(hostInstructors);
  }
  for (const key of view.partnerKeys) {
    add(instructorsByPartner?.[key]);
  }
  return [...byId.values()];
}

export function derivePartnerLocations(sessions: unknown[] | null | undefined): PartnerLocationOption[] {
  const byKey = new Map<string, PartnerLocationOption>();
  for (const session of sessions ?? []) {
    if (!isPartnerArtifact(session)) {
      continue;
    }
    const key = partnerScheduleKey(session);
    if (!key || byKey.has(key)) {
      continue;
    }
    byKey.set(key, {
      key,
      handle: partnerHandle(session),
      partnerLocationId: partnerLocationId(session),
      name: partnerDisplayName(session),
    });
  }

  const nameCounts = new Map<string, number>();
  for (const option of byKey.values()) {
    nameCounts.set(option.name, (nameCounts.get(option.name) ?? 0) + 1);
  }
  for (const option of byKey.values()) {
    if ((nameCounts.get(option.name) ?? 0) > 1 && option.partnerLocationId != null) {
      option.name = `${option.name} (${option.partnerLocationId})`;
    }
  }

  return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function syntheticStudioForPartner(option: PartnerLocationOption, hostLocationId: number): Studio {
  return {
    Id: syntheticStudioIdForKey(option.key),
    Name: option.name,
    Description: '',
    Color: '',
    Room: null,
    RoomId: 0,
    State: Status.Active,
    LocationId: hostLocationId,
    IsPartnerArtifact: true,
    PartnerHandle: option.handle,
    PartnerLocationId: option.partnerLocationId ?? undefined,
    Image: undefined as unknown as Studio['Image'],
    Guid: '',
    CreatedAt: new Date(0),
    CreatedBy: '',
    LastModified: new Date(0),
    LastModifiedBy: '',
  };
}

export function instructorsFromPartnerSessions(sessions: Session[] | null | undefined): InstructorDetails[] {
  const byId = new Map<number, InstructorDetails>();
  for (const session of sessions ?? []) {
    if (!isPartnerArtifact(session)) {
      continue;
    }
    const id = Number(session.InstructorId);
    if (!Number.isFinite(id) || id <= 0 || byId.has(id)) {
      continue;
    }
    const name = (session.InstructorName || '').trim();
    const parts = name.split(/\s+/).filter(part => part.length > 0);
    byId.set(id, {
      Id: id,
      State: EmployeeState.Active,
      LocationId: session.LocationId,
      IsPartnerArtifact: true,
      PartnerHandle: partnerHandle(session),
      PartnerLocationId: partnerLocationId(session) ?? undefined,
      Person: {
        FirstName: parts[0] || name,
        LastName: parts.slice(1).join(' '),
        FullName: name,
      },
      Avatar: session.InstructorImageUrl ? { UploadedUrl: session.InstructorImageUrl } : undefined,
    } as InstructorDetails);
  }
  return [...byId.values()];
}

/** Positive studio ids are real host studios. Synthetic partner studios are negative. */
export function apiStudioIds(studioIds: number[] | null | undefined): number[] {
  return (studioIds ?? []).filter(id => id > 0);
}

/**
 * Studio catalog for encoding StudioIds into the schedule URL.
 * Always include host studios so a partner→host modal save still resolves host IDs
 * while `this.studios` still reflects the previous partner view.
 */
export function studiosForScheduleUrl(
  hostStudios: Studio[] | null | undefined,
  currentStudios: Studio[] | null | undefined,
  studioByPartner: Record<string, Studio> | null | undefined
): Studio[] {
  return [
    ...(hostStudios ?? []),
    ...(currentStudios ?? []),
    ...Object.values(studioByPartner ?? {}),
  ];
}

/**
 * Instructor catalog for encoding InstructorIds into the schedule URL.
 * Same host-inclusion rationale as {@link studiosForScheduleUrl}.
 */
export function instructorsForScheduleUrl(
  hostInstructors: InstructorDetails[] | null | undefined,
  currentInstructors: InstructorDetails[] | null | undefined,
  instructorsByPartner: Record<string, InstructorDetails[]> | null | undefined
): InstructorDetails[] {
  return [
    ...(hostInstructors ?? []),
    ...(currentInstructors ?? []),
    ...Object.values(instructorsByPartner ?? {}).flat(),
  ];
}

/** Same slug rules as schedule URL encoding historically used for instructor names. */
function instructorNameSlug(fullName: string | null | undefined): string {
  if (!fullName) {
    return '';
  }
  return fullName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-+/g, '-')
    .replace(/^\-+|\-+$/g, '');
}

/**
 * Encode selected instructor filters as stable numeric ID tokens.
 * Name slugs collide when a host instructor and a partner artifact share FullName.
 */
export function instructorTokensForScheduleUrl(
  instructorIds: number[] | null | undefined,
  instructors: InstructorDetails[] | null | undefined
): string[] {
  const catalog = new Map((instructors ?? []).map(instructor => [instructor.Id, instructor]));
  const tokens: string[] = [];
  const seen = new Set<number>();
  for (const id of instructorIds ?? []) {
    if (!catalog.has(id) || seen.has(id)) {
      continue;
    }
    seen.add(id);
    tokens.push(String(id));
  }
  return tokens;
}

/**
 * Resolve instructor URL tokens to IDs. Numeric tokens win; legacy name slugs still work
 * (first catalog match, so host-before-partner catalogs prefer the host on ambiguous names).
 */
export function resolveInstructorIdsFromTokens(
  tokens: string[] | null | undefined,
  instructors: InstructorDetails[] | null | undefined
): number[] {
  const rows = instructors ?? [];
  const byId = new Map(rows.map(instructor => [instructor.Id, instructor]));
  const byNameSlug = new Map<string, number>();
  for (const instructor of rows) {
    const slug = instructorNameSlug(instructor.Person?.FullName);
    if (slug && !byNameSlug.has(slug)) {
      byNameSlug.set(slug, instructor.Id);
    }
  }

  const ids: number[] = [];
  const seen = new Set<number>();
  for (const raw of tokens ?? []) {
    const token = (raw || '').trim();
    if (!token) {
      continue;
    }
    const asId = parseInt(token, 10);
    let resolved: number | undefined;
    if (!Number.isNaN(asId) && byId.has(asId)) {
      resolved = asId;
    } else {
      resolved = byNameSlug.get(token.toLowerCase());
    }
    if (resolved == null || seen.has(resolved)) {
      continue;
    }
    seen.add(resolved);
    ids.push(resolved);
  }
  return ids;
}

/** Clears studio, instructor, and address selections. Location choices are independent of these. */
export function clearOwnershipScopedFilters(filterModel: {
  StudioIds: number[];
  InstructorIds: number[];
  AddressIds: number[];
}): void {
  filterModel.StudioIds = [];
  filterModel.InstructorIds = [];
  filterModel.AddressIds = [];
}

/** Host addresses apply whenever the host schedule is part of the selection, including mixed views. */
export function addressesForSelectedSchedule<T>(
  addresses: T[] | null | undefined,
  selection: ScheduleViewSelection | string | null | undefined
): T[] {
  if (!selectionIncludesHost(selection)) {
    return [];
  }
  return addresses ?? [];
}
